import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../hooks/useAuth';
import { usePair } from '../hooks/usePair';
import { useMeetings } from '../hooks/useMeetings';

const { FiCalendar, FiClock, FiMapPin, FiPlus, FiEdit3, FiTrash2, FiSave, FiX, FiAlertCircle } = FiIcons;

function MeetingForm({ initial, onSubmit, onCancel, loading }) {
  const [formData, setFormData] = useState(
    initial || { scheduled_date: '', scheduled_time: '', location: '', notes: '' }
  );

  const handle = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  const submit = (e) => { e.preventDefault(); onSubmit(formData); };

  return (
    <form onSubmit={submit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date</label>
          <div className="relative">
            <SafeIcon icon={FiCalendar} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="date"
              name="scheduled_date"
              value={formData.scheduled_date}
              onChange={handle}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 bg-transparent dark:bg-gray-900 dark:text-white rounded-lg custom-focus transition-colors"
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Time</label>
          <div className="relative">
            <SafeIcon icon={FiClock} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="time"
              name="scheduled_time"
              value={formData.scheduled_time}
              onChange={handle}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 bg-transparent dark:bg-gray-900 dark:text-white rounded-lg custom-focus transition-colors"
              required
            />
          </div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Location</label>
        <div className="relative">
          <SafeIcon icon={FiMapPin} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handle}
            placeholder="Coffee shop, church, home, etc."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 bg-transparent dark:bg-gray-900 dark:text-white dark:placeholder-gray-500 rounded-lg custom-focus transition-colors"
            required
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Notes (Optional)</label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handle}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-transparent dark:bg-gray-900 dark:text-white dark:placeholder-gray-500 rounded-lg custom-focus transition-colors"
          placeholder="Week topic, special instructions, etc."
        />
      </div>
      <div className="flex space-x-4">
        <button
          type="submit"
          disabled={loading}
          className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-2 disabled:opacity-60"
        >
          {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <SafeIcon icon={FiSave} className="w-4 h-4" />}
          <span>{loading ? 'Saving…' : initial ? 'Update Meeting' : 'Schedule Meeting'}</span>
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-6 py-3 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center space-x-2"
        >
          <SafeIcon icon={FiX} className="w-4 h-4" />
          <span>Cancel</span>
        </button>
      </div>
    </form>
  );
}

function MeetingCard({ meeting, userRole, onEdit, onDelete }) {
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const formatTime = (t) => new Date(`2000-01-01T${t}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-primary bg-opacity-10 dark:bg-opacity-20 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiCalendar} className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{formatDate(meeting.scheduled_date)}</h3>
              <p className="text-gray-600 dark:text-gray-400">{formatTime(meeting.scheduled_time)}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiMapPin} className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              <span className="text-gray-700 dark:text-gray-300">{meeting.location}</span>
            </div>
            {meeting.notes && (
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiEdit3} className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5" />
                <span className="text-gray-700 dark:text-gray-300">{meeting.notes}</span>
              </div>
            )}
          </div>
        </div>
        {userRole === 'leader' && (
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={() => onEdit(meeting)}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-primary dark:hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiEdit3} className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(meeting.id)}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function MeetingScheduler({ userRole }) {
  const { user } = useAuth();
  const { pair, loading: pairLoading } = usePair(user?.id, userRole);
  const { meetings, loading, addMeeting, editMeeting, removeMeeting } = useMeetings(pair?.id, user?.id);

  const [isCreating, setIsCreating] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async (formData) => {
    setSaving(true);
    setError('');
    try {
      await addMeeting(formData);
      setIsCreating(false);
    } catch (err) {
      setError(err.message || 'Failed to schedule meeting.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (formData) => {
    setSaving(true);
    setError('');
    try {
      await editMeeting(editingMeeting.id, formData);
      setEditingMeeting(null);
    } catch (err) {
      setError(err.message || 'Failed to update meeting.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await removeMeeting(id);
    } catch (err) {
      setError(err.message || 'Failed to delete meeting.');
    }
  };

  const noPair = !pairLoading && !pair;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 transition-colors duration-200">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-space font-bold text-gray-900 dark:text-white mb-2">Meeting Scheduler</h1>
            <p className="text-gray-600 dark:text-gray-400">
              {userRole === 'leader' ? 'Schedule and manage your discipleship meetings.' : 'View your upcoming discipleship meetings.'}
            </p>
          </div>
          {userRole === 'leader' && !isCreating && !editingMeeting && pair && (
            <button
              onClick={() => setIsCreating(true)}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              <span>Schedule Meeting</span>
            </button>
          )}
        </div>
      </motion.div>

      {/* No pair warning */}
      {noPair && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 rounded-xl p-4 mb-6 flex items-start gap-3 transition-colors">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            {userRole === 'leader'
              ? 'You need an active discipleship pair to schedule meetings. Create a pair first from your profile.'
              : "You're not linked to a discipleship pair yet. Ask your leader to add you."}
          </p>
        </div>
      )}

      {error && (
        <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50 rounded-lg text-sm text-red-600 dark:text-red-400 flex items-center gap-2 transition-colors">
          <SafeIcon icon={FiAlertCircle} className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Create form */}
      {isCreating && userRole === 'leader' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-8 transition-colors">
          <h2 className="text-xl font-space font-semibold text-gray-900 dark:text-white mb-6">Schedule New Meeting</h2>
          <MeetingForm onSubmit={handleCreate} onCancel={() => setIsCreating(false)} loading={saving} />
        </motion.div>
      )}

      {/* Edit form */}
      {editingMeeting && userRole === 'leader' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-8 transition-colors">
          <h2 className="text-xl font-space font-semibold text-gray-900 dark:text-white mb-6">Edit Meeting</h2>
          <MeetingForm
            initial={{
              scheduled_date: editingMeeting.scheduled_date,
              scheduled_time: editingMeeting.scheduled_time,
              location: editingMeeting.location,
              notes: editingMeeting.notes,
            }}
            onSubmit={handleEdit}
            onCancel={() => setEditingMeeting(null)}
            loading={saving}
          />
        </motion.div>
      )}

      {/* Meeting list */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-4">
        {loading || pairLoading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">Loading meetings…</p>
          </div>
        ) : meetings.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-12 text-center transition-colors">
            <SafeIcon icon={FiCalendar} className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No meetings scheduled</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {userRole === 'leader'
                ? pair ? 'Start by scheduling your first discipleship meeting.' : 'Create a pair first to schedule meetings.'
                : "Your leader hasn't scheduled any meetings yet."}
            </p>
            {userRole === 'leader' && pair && (
              <button
                onClick={() => setIsCreating(true)}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
              >
                Schedule First Meeting
              </button>
            )}
          </div>
        ) : (
          meetings.map((meeting) => (
            <MeetingCard
              key={meeting.id}
              meeting={meeting}
              userRole={userRole}
              onEdit={setEditingMeeting}
              onDelete={handleDelete}
            />
          ))
        )}
      </motion.div>
    </div>
  );
}

export default MeetingScheduler;